/**
 * Shopify credential inputs the browser and password managers must leave alone.
 *
 * TEMPORARY local copy: the shared `SecretInput` (`@/components/ui/secret-input`)
 * replaces {@link LocalSecretInput} once it lands, so its props stay
 * compatible: `revealable`, `wrapperClassName`, and the normal input props
 * minus `type` / `autoComplete`.
 *
 * Why every Shopify field (domain, Client ID, secret, access token) carries the
 * same props: Chrome groups every input on the page into one form, so a single
 * leftover password field pulls the user's email into the field before it.
 * `autoComplete="one-time-code"` makes Chrome treat the field as not a
 * password (no "Suggest strong password", no "Save password?"); the data-*
 * attributes opt out of 1Password, LastPass, Bitwarden and Dashlane.
 */
import { forwardRef, useState, type ComponentPropsWithoutRef } from 'react';
import { Eye, EyeOff } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

const NO_AUTOFILL_PROPS = {
  autoComplete: 'one-time-code',
  'data-1p-ignore': true,
  'data-lpignore': 'true',
  'data-bwignore': true,
  'data-form-type': 'other',
} as const;

type BaseInputProps = Omit<ComponentPropsWithoutRef<typeof Input>, 'type' | 'autoComplete'>;

/** A plain text field (shop domain, Client ID) that nothing autofills. */
export const NoAutofillTextInput = forwardRef<HTMLInputElement, BaseInputProps>(function NoAutofillTextInput(
  props,
  ref,
) {
  return <Input ref={ref} type="text" spellCheck={false} autoCapitalize="off" autoCorrect="off" {...props} {...NO_AUTOFILL_PROPS} />;
});

export interface LocalSecretInputProps extends BaseInputProps {
  /** Show an eye toggle that reveals the value. */
  revealable?: boolean;
  /** Class for the wrapping element (the input itself takes `className`). */
  wrapperClassName?: string;
}

/** A masked secret field (Client Secret, access token) that nothing autofills or offers to save. */
export const LocalSecretInput = forwardRef<HTMLInputElement, LocalSecretInputProps>(function LocalSecretInput(
  { revealable = false, wrapperClassName, className, ...props },
  ref,
) {
  const { t } = useTranslation('settings');
  const [revealed, setRevealed] = useState(false);
  return (
    <div className={cn('relative', wrapperClassName)}>
      <Input
        ref={ref}
        type={revealable && revealed ? 'text' : 'password'}
        spellCheck={false}
        autoCapitalize="off"
        autoCorrect="off"
        className={cn(revealable && 'pr-10', className)}
        {...props}
        {...NO_AUTOFILL_PROPS}
      />
      {revealable && (
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="absolute right-0 top-0 h-full px-3"
          aria-label={revealed ? t('shopifyConnection.store.form.hideValue') : t('shopifyConnection.store.form.showValue')}
          aria-pressed={revealed}
          onClick={() => setRevealed((value) => !value)}
          disabled={props.disabled}
        >
          {revealed ? <EyeOff className="h-4 w-4" aria-hidden="true" /> : <Eye className="h-4 w-4" aria-hidden="true" />}
        </Button>
      )}
    </div>
  );
});
